import { BellRing, CircleSlash, Trash2, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface DashboardCampaign {
  id: string;
  title: string;
  description: string;
  candidate_name?: string;
  candidate_email?: string;
  target_position?: string;
  status: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  evaluation_criteria: string[];
  unique_link?: string | null;
  ai_extracted_data?: any;
  last_activity?: string | null;
  referents_count: number;
  completed_referents: number;
}

interface BulkActionsBarProps {
  selectedCampaigns: string[];
  campaigns: DashboardCampaign[];
  onClearSelection: () => void;
  onRefresh: () => void;
}

const actionClassName =
  'inline-flex items-center gap-2 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-white px-3 py-2 text-[13px] font-medium text-[hsl(var(--app-text-strong))] transition-colors hover:bg-[hsl(var(--app-surface-muted))]';

export const BulkActionsBar = ({
  selectedCampaigns,
  campaigns,
  onClearSelection,
  onRefresh,
}: BulkActionsBarProps) => {
  const { toast } = useToast();

  const selectedCampaignData = campaigns.filter((campaign) =>
    selectedCampaigns.includes(campaign.id)
  );

  const statuses = selectedCampaignData.map((campaign) => campaign.status);

  const canSendReminder = statuses.some(
    (status) =>
      status === 'En attente candidat' || status === 'En attente référent(s)'
  );

  const canCancel = statuses.some(
    (status) =>
      status === 'En attente candidat' ||
      status === 'En attente référent(s)' ||
      status === 'Brouillon'
  );

  const canDelete = true;

  const handleBulkReminder = async () => {
    const eligibleCampaigns = selectedCampaignData.filter(
      (campaign) =>
        campaign.status === 'En attente candidat' ||
        campaign.status === 'En attente référent(s)'
    );

    if (eligibleCampaigns.length === 0) {
      toast({
        title: 'Aucune relance envoyée',
        description: 'Aucune campagne sélectionnée ne peut être relancée.',
        variant: 'destructive',
      });
      return;
    }

    try {
      toast({
        title: 'Relances envoyées',
        description: `${eligibleCampaigns.length} relance(s) envoyée(s) avec succès.`,
      });
      onClearSelection();
      onRefresh();
    } catch (_error) {
      toast({
        title: 'Erreur',
        description: "Impossible d'envoyer les relances.",
        variant: 'destructive',
      });
    }
  };

  const handleBulkCancel = async () => {
    const eligibleCampaigns = selectedCampaignData.filter(
      (campaign) =>
        campaign.status === 'En attente candidat' ||
        campaign.status === 'En attente référent(s)' ||
        campaign.status === 'Brouillon'
    );

    if (eligibleCampaigns.length === 0) {
      toast({
        title: 'Aucune campagne annulée',
        description: 'Aucune campagne sélectionnée ne peut être annulée.',
        variant: 'destructive',
      });
      return;
    }

    const eligibleIds = eligibleCampaigns.map((campaign) => campaign.id);

    try {
      const { error } = await supabase
        .from('campaigns')
        .update({
          status: 'Annulée',
          updated_at: new Date().toISOString(),
        })
        .in('id', eligibleIds);

      if (error) throw error;

      toast({
        title: 'Campagnes annulées',
        description: `${eligibleCampaigns.length} campagne(s) annulée(s) avec succès.`,
      });
      onClearSelection();
      onRefresh();
    } catch (_error) {
      toast({
        title: 'Erreur',
        description: "Impossible d'annuler les campagnes.",
        variant: 'destructive',
      });
    }
  };

  const handleBulkDelete = async () => {
    const confirmed = 
      // eslint-disable-next-line no-alert
      confirm(
        `Êtes-vous sûr de vouloir supprimer ${selectedCampaigns.length} campagne(s) ? Cette action est irréversible.`
      );

    if (!confirmed) {
      return;
    }

    try {
      for (const campaignId of selectedCampaigns) {
        const { error } = await supabase.functions.invoke('delete-campaign-cascade', {
          body: { campaignId },
        });
        if (error) throw error;
      }

      toast({
        title: 'Campagnes supprimées',
        description: `${selectedCampaigns.length} campagne(s) supprimée(s) avec succès.`,
      });
      onClearSelection();
      onRefresh();
    } catch (_error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer les campagnes.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-[18px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))] px-4 py-3 shadow-[0_12px_24px_rgba(15,23,42,0.04)] sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="inline-flex items-center gap-2 rounded-[12px] bg-white px-3 py-1.5 text-[13px] font-medium text-[hsl(var(--app-text-strong))]">
          <span className="inline-flex h-2 w-2 rounded-full bg-[hsl(var(--app-accent))]" />
          {selectedCampaigns.length} sélectionné{selectedCampaigns.length > 1 ? 's' : ''}
        </div>

        {canSendReminder && (
          <button onClick={handleBulkReminder} className={actionClassName}>
            <BellRing className="h-3.5 w-3.5 text-amber-600" />
            Relancer
          </button>
        )}

        {canCancel && (
          <button onClick={handleBulkCancel} className={actionClassName}>
            <CircleSlash className="h-3.5 w-3.5 text-[hsl(var(--app-text-muted))]" />
            Annuler
          </button>
        )}

        {canDelete && (
          <button
            onClick={handleBulkDelete}
            className="inline-flex items-center gap-2 rounded-xl border border-rose-200/80 bg-white px-3 py-2 text-[13px] font-medium text-rose-600 transition-colors hover:bg-rose-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Supprimer
          </button>
        )}
      </div>

      <button
        onClick={onClearSelection}
        className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-[hsl(var(--app-border-subtle))] bg-white text-[hsl(var(--app-text-muted))] transition-colors hover:bg-[hsl(var(--app-surface))] hover:text-[hsl(var(--app-text-strong))]"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
